import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:translate-y-[1px]",
  {
    variants: {
      variant: {
        // Primary: Solid background with layered shadow, used for main CTAs
        default:
          "bg-primary text-primary-foreground shadow-[0_1px_2px_rgba(0,0,0,0.05),0_4px_12px_hsl(var(--primary)/0.25)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_8px_24px_hsl(var(--primary)/0.35)] hover:brightness-110 active:shadow-[0_1px_2px_rgba(0,0,0,0.05),0_2px_8px_hsl(var(--primary)/0.2)]",
        // Primary gradient: For hero CTAs only with enhanced glow
        gradient:
          "gradient-bg text-primary-foreground shadow-[0_2px_4px_rgba(0,0,0,0.05),0_8px_24px_hsl(var(--primary)/0.3)] hover:shadow-[0_4px_8px_rgba(0,0,0,0.05),0_16px_48px_hsl(var(--primary)/0.4)] hover:brightness-105 active:shadow-[0_1px_2px_rgba(0,0,0,0.05),0_4px_12px_hsl(var(--primary)/0.25)]",
        // Destructive with proper shadow
        destructive:
          "bg-destructive text-destructive-foreground shadow-[0_1px_2px_rgba(0,0,0,0.05),0_4px_12px_hsl(var(--destructive)/0.2)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_8px_20px_hsl(var(--destructive)/0.3)] hover:bg-destructive/90",
        // Secondary: Subtle background with soft shadow
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow",
        // Outline: Border with subtle hover enhancement
        outline:
          "border border-border bg-transparent hover:bg-muted hover:text-foreground hover:border-border/80 hover:shadow-sm",
        // Ghost: No background, subtle hover
        ghost: "hover:bg-muted hover:text-foreground",
        // Link: Underline on hover
        link: "text-primary underline-offset-4 hover:underline",
        // Premium: Glass effect for special sections
        glass:
          "bg-background/10 backdrop-blur-sm border border-border/50 text-foreground hover:bg-background/20 hover:border-border shadow-sm",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-lg px-4 text-sm",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-base font-semibold tracking-tight",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
